import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  ALL_REPORT_FILTERS,
  ARCHIVED_OPTIONS,
  BILLED_OPTIONS,
  reportPeriods,
} from '@/constants/reportFilterConstants';
import type {
  ReportFilterPanelContext,
  ReportFilterState,
  ReportOption,
  ReportPeriod,
  ReportQuery,
  ReportQueryDialect,
  ReportShowFilters,
  ReportTrustAccount,
  ReportUser,
} from '@/models/reportFilterTypes';
import { buildReportQuery, legacyFilterToState, visibleFilterState } from '@/utils/reportQueryHelpers';

/**
 * The single source of truth for report filters, replacing three overlapping
 * module-level singletons:
 *
 *   - `commonFilterService`  — broadcast bus for the 43 standard reports
 *   - `reportFilterService`  — `_state` object for the generic engine
 *   - `filterService`'s `globalReportsScopeAccount` — cross-family "Show Matter
 *     Involving" sync, the reason the other two were already coupled
 *
 * Plain Pinia, not Colada: this holds filter state, it does not fetch. The reports
 * themselves do the fetching. See `docs/REPORT_REPLATFORMING_TRIAGE.md`.
 */
export const useReportFiltersStore = defineStore('reportFilters', () => {
  const periodOptions = ref<ReportPeriod[]>(reportPeriods(true));
  const billedOptions = ref<ReportOption[]>([...BILLED_OPTIONS]);
  const archivedOptions = ref<ReportOption[]>([...ARCHIVED_OPTIONS]);

  const defaultState = (): ReportFilterState => ({
    dateRange: periodOptions.value[0],
    // Two billed filters exist because the contingency reports default differently.
    billed: billedOptions.value.find((o) => o.id === 'all') ?? null,
    contingencyBilled: billedOptions.value.find((o) => o.id === 'WIP') ?? null,
    status: archivedOptions.value[1],
    matterTag: null,
    trustAccount: null,
    account: null,
    scopeAccount: null,
    primary: false,
    originating: false,
    reviewer: false,
    distributionId: null,
    distributionStatus: null,
    showEntries: true,
    showCharges: true,
    showExpenses: true,
    showExpense: false,
    includeBilled: false,
    originationUser: false,
    billedValue: null,
  });

  const filters = ref<ReportFilterState>(defaultState());

  /**
   * Whether the user has touched "Show Matter Involving" at all. Distinct from
   * `scopeAccount === null`, which is a deliberate "no filter" the store must honour
   * instead of re-applying the role default. Mirrors `filterService`'s `isChanged`.
   */
  const scopeAccountTouched = ref(false);

  /** The role default, which is what a report hiding "Show Matters Involving" gets. */
  const defaultScopeAccount = ref<ReportUser | null>(null);

  /** Set by whichever directive hosts `ReportFilterPanel` — see `configurePanel`. */
  const panel = ref<ReportFilterPanelContext>({
    family: 'generic',
    show: {},
    accounts: [],
    lockAccountSelection: false,
    distributions: [],
    trustAccounts: [],
    printFilterTime: null,
  });

  function configurePanel(patch: Partial<ReportFilterPanelContext>) {
    Object.assign(panel.value, patch);
  }

  /** Index-aligned with `periodOptions`, which use short month labels. */
  function periodFromRoute(idOrLabel: string | null | undefined): ReportPeriod | undefined {
    if (!idOrLabel) return undefined;
    const indexIn = (list: ReportPeriod[]) => list.findIndex((p) => p.id === idOrLabel || p.label === idOrLabel);
    const short = indexIn(periodOptions.value);
    const index = short >= 0 ? short : indexIn(reportPeriods(false));
    return index >= 0 ? periodOptions.value[index] : undefined;
  }

  const sameWindow = (a: ReportPeriod, b: ReportPeriod) =>
    a.label === b.label &&
    (a.range.startDate?.getTime() ?? null) === (b.range.startDate?.getTime() ?? null) &&
    (a.range.endDate?.getTime() ?? null) === (b.range.endDate?.getTime() ?? null);

  /**
   * Period windows are relative to today, so a tab left open past midnight or into a new
   * month would keep querying the old ones. Rebuilds them in place, keeps the selection
   * on the same entry, and keeps a custom period's dates. No-op when nothing moved.
   */
  function refreshPeriods(now: Date = new Date()) {
    const fresh = reportPeriods(true, now);
    const current = periodOptions.value;
    const customDates = current.find((p) => p.custom)?.range;
    const freshCustom = fresh.find((p) => p.custom);
    if (customDates && freshCustom) freshCustom.range = { ...customDates };
    if (fresh.length === current.length && fresh.every((p, i) => sameWindow(p, current[i]))) return;

    const selected = filters.value.dateRange;
    const index = selected ? current.indexOf(selected) : -1;
    current.splice(0, current.length, ...fresh);
    if (index >= 0) filters.value.dateRange = current[index];
  }

  const hasDistribution = computed(() => Boolean(filters.value.distributionId));

  /** A chosen distribution defines its own period, so the date select is disabled. */
  const dateRangeDisabled = computed(() => hasDistribution.value);

  /**
   * Mutates in place, never reassigns. The AngularJS generic controller takes a
   * reference to this object once at construction and `$watch`es it deeply — replacing
   * it would leave that controller watching a detached object and silently stop the
   * report reloading on filter changes.
   */
  function setFilters(patch: Partial<ReportFilterState>) {
    Object.assign(filters.value, patch);
  }

  function setScopeAccount(account: ReportUser | null) {
    filters.value.scopeAccount = account;
    scopeAccountTouched.value = true;
  }

  /**
   * Applies the "Show Matter Involving" default the first time a report is opened:
   * operators and accountants see the whole firm, everyone else defaults to their own
   * matters. A user's explicit choice — including clearing it — always wins.
   */
  function applyScopeAccountDefault(me: ReportUser | null, availableAccounts: ReportUser[], isFirmWideRole: boolean) {
    defaultScopeAccount.value = isFirmWideRole ? null : me;
    if (scopeAccountTouched.value) {
      const saved = filters.value.scopeAccount;
      if (!saved) return;
      filters.value.scopeAccount = availableAccounts.find((a) => a.id === saved.id) ?? null;
      return;
    }
    filters.value.scopeAccount = isFirmWideRole ? null : me;
  }

  /** Writes onto the selected custom period itself, which is how it keeps the user's dates. */
  function setCustomDate(edge: 'startDate' | 'endDate', value: Date | string | null | undefined) {
    const period = filters.value.dateRange;
    if (!period?.custom) return;
    const next = value ? new Date(value) : null;
    // LlDatePicker echoes back whatever it is given; a fresh Date for the same moment
    // would bounce between the two forever.
    if ((next?.getTime() ?? null) === (period.range[edge]?.getTime() ?? null)) return;
    period.range[edge] = next;
  }

  function setTrustAccount(account: ReportTrustAccount | null) {
    filters.value.trustAccount = account;
  }

  /** Pre-selects a period from a `?dateRangeId` deep link, by id or by label. */
  function selectPeriod(idOrLabel: string | null | undefined) {
    const period = periodFromRoute(idOrLabel);
    if (period) filters.value.dateRange = period;
  }

  /**
   * Selecting a distribution replaces the period, so a report showing billed totals
   * for a date range would be showing them for the wrong window.
   */
  function setDistribution(distributionId: string | null, revenueReport = true) {
    filters.value.distributionId = distributionId;
    if (revenueReport && distributionId) filters.value.showExpense = false;
  }

  function reset() {
    Object.assign(filters.value, defaultState());
    scopeAccountTouched.value = false;
    defaultScopeAccount.value = null;
  }

  /**
   * A hidden "Show Matters Involving" falls back to the role default, except after a
   * deliberate "no filter": that widens rather than narrows, and is what `main` sent.
   */
  function visibleFilters(showFilters?: ReportShowFilters): ReportFilterState {
    const choseEveryone = scopeAccountTouched.value && !filters.value.scopeAccount;
    return visibleFilterState(filters.value, showFilters, {
      ...defaultState(),
      scopeAccount: choseEveryone ? null : defaultScopeAccount.value,
    });
  }

  /** `visibleOnly` is the Vue panel's behaviour; the legacy templates send everything. */
  function query(
    showFilters?: ReportShowFilters,
    dialect: ReportQueryDialect = 'generic',
    visibleOnly = false,
  ): ReportQuery {
    const state = visibleOnly ? visibleFilters(showFilters) : filters.value;
    return buildReportQuery(state, showFilters, dialect);
  }

  /**
   * Entry point for the standard reports, whose `commonFilter` directive still owns its
   * own `$scope.filter`. Normalising it in here is what keeps the two families' wire
   * shape in one place while they migrate one at a time.
   */
  function queryFromLegacyFilter(legacyFilter: Record<string, unknown> | null | undefined): ReportQuery {
    return buildReportQuery(legacyFilterToState(legacyFilter), ALL_REPORT_FILTERS, 'standard');
  }

  /** Mirrors the legacy filter into the store so both families read one scope account. */
  function adoptLegacyFilter(legacyFilter: Record<string, unknown> | null | undefined) {
    Object.assign(filters.value, legacyFilterToState(legacyFilter));
  }

  return {
    filters,
    scopeAccountTouched,
    panel,
    configurePanel,
    periodFromRoute,
    refreshPeriods,
    periodOptions,
    billedOptions,
    archivedOptions,
    hasDistribution,
    dateRangeDisabled,
    setFilters,
    setScopeAccount,
    applyScopeAccountDefault,
    setTrustAccount,
    setCustomDate,
    selectPeriod,
    setDistribution,
    reset,
    visibleFilters,
    query,
    queryFromLegacyFilter,
    adoptLegacyFilter,
  };
});
